import { D } from '@mobily/ts-belt';
import type { ReportSelectEventData } from '@stitch-fix/event-reporter';
import { useSelectEvent } from '@stitch-fix/event-reporter';
import { Text, ModalFooter } from '@stitch-fix/mode-react';
import { UseControllerProps, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ControlledCheckboxGroup } from '../Forms/ControlledCheckboxGroup';
import { ControlledTextField } from '../Forms/ControlledTextField';
type MultiSelectOption = {
  label: string;
  value: string;
};
export type MultiSelectQuestion = {
  label: string;
  questionId: string;
  options: MultiSelectOption[];
};
export type TextQuestion = {
  questionId: string;
  label: string;
};
export type RemoveItemFormProps = {
  questions: {
    reasonSelections: MultiSelectQuestion[];
    otherComments: TextQuestion[];
  };
  onSuccessfulValidation: (answerData: RemoveItemFormValues) => void;
  onCloseModal: () => void;
};
export type RemoveItemFormValues = {
  reasonSelections: string[];
  otherComments: string;
};
type RemoveItemFormInputSchema = z.input<typeof removeItemFormSchema>;
type RemoveItemFormOutputSchema = z.infer<typeof removeItemFormSchema>;
const removeItemFormSchema = z.object({
  reasonSelections: z.array(z.string()).optional().default([]),
  otherComments: z.string().optional().default('')
});

/**
 * Building Multiple Choice Checkbox Options for reason selections
 */
const buildMultipleChoiceQuestion = (question: MultiSelectQuestion, control: UseControllerProps<RemoveItemFormInputSchema>['control'], name: 'reasonSelections') => <>
    <Text mb={1} setting="title-xsmall">
      {question?.label}
    </Text>
    <ControlledCheckboxGroup name={name} options={question?.options} control={control} />
  </>;

/**
 * Building Text field questions for other comments
 */
const buildTextFieldQuestion = (question: TextQuestion, control: UseControllerProps<RemoveItemFormInputSchema>['control'], name: 'otherComments') => <ControlledTextField control={control} name={name} label={question?.label} rows={4} />;

/**
 * Select event object when a user submits a form to remove bought item
 */
const confirmRemoveBoughtItemEvent: ReportSelectEventData = {
  schema: 'select',
  name: 'remove_from_bought',
  action_name: 'confirm_remove_kept_item'
};

/**
 * Renders the Remove Item Feedback form with multiple choice questions and
 * a text field question. It handles the form submission logic and returns it back
 * to the hideItemFeedback mutation.
 */
export const RemoveItemForm = ({
  questions,
  onSuccessfulValidation,
  onCloseModal
}: RemoveItemFormProps) => {
  const {
    handleSubmit,
    control
  } = useForm<RemoveItemFormInputSchema, unknown, RemoveItemFormOutputSchema>({
    resolver: zodResolver(removeItemFormSchema)
  });
  const {
    reportSelectEvent
  } = useSelectEvent();
  const validatedSubmit = (data: RemoveItemFormValues) => {
    reportSelectEvent(confirmRemoveBoughtItemEvent);
    onSuccessfulValidation(data);
  };

  // Currently the hideFeedbackQuestions only returns one question
  // for reasonSelections and otherComments
  const {
    reasonSelections,
    otherComments
  } = questions;
  return <form onSubmit={handleSubmit(validatedSubmit)}>
      {D.isNotEmpty(reasonSelections[0]) && buildMultipleChoiceQuestion(reasonSelections[0], control, 'reasonSelections')}

      {D.isNotEmpty(otherComments[0]) && buildTextFieldQuestion(otherComments[0], control, 'otherComments')}
      <ModalFooter primaryAction={{
      text: 'Remove now',
      variant: 'filled-staple',
      type: 'submit'
    }} secondaryAction={{
      text: "No, Don't Remove it",
      onClick: onCloseModal
    }} />
    </form>;
};